/**
 * Crafting recipes (vanilla shaped + shapeless, restricted to blocks/items that
 * exist in this engine) and the matcher used by the 2x2 / 3x3 crafting grids.
 *
 *   craft(grid, w, h)        result Stack for a w*h row-major grid, or null
 *   consumeCraft(grid, w, h) removes one of every used ingredient in place
 *   CRAFTING_RECIPES         the full recipe list (UI / recipe-book use)
 *
 * Shaped recipes are position independent inside the grid and may be mirrored.
 * An ingredient is an exact id or an id set (vanilla tags: any planks, any log...).
 * Only the lead's data modules import this, so it stays DOM-free.
 */

import * as B from '../blocks';
import { RS } from '../world/redstone/defs';
import { SIGN_IDS, UTIL } from './utility/defs';
import { I } from './itemIds';
import { dyeColour } from './fireworks';
import { cloneStack, clearStack, isEmpty, stackOf, type Stack } from './types';
import { SPEAR_KEYS } from './spear';
import { registerBrewingRecipes } from './brewing/recipes';

/** Exact id, or a tag (any id in the list); 0 = the cell must be empty. */
export type Ingredient = number | readonly number[];

export interface Recipe {
  shaped: boolean;
  /** Shaped bounds (0 for shapeless). */
  w: number;
  h: number;
  /** Shaped: row-major w*h; shapeless: the ingredient list. */
  ing: readonly Ingredient[];
  result: Stack;
}

const named = (n: string): number => B.BLOCKS.find((b) => b?.name === n)?.id ?? -1;

// --- tags ----------------------------------------------------------------------

const WOODS = [
  { log: B.LOG_ID, planks: B.PLANKS_ID },
  { log: B.SPRUCE_LOG_ID, planks: B.SPRUCE_PLANKS_ID },
  { log: B.BIRCH_LOG_ID, planks: B.BIRCH_PLANKS_ID },
  { log: B.JUNGLE_LOG_ID, planks: B.JUNGLE_PLANKS_ID },
  { log: B.ACACIA_LOG_ID, planks: B.ACACIA_PLANKS_ID },
  { log: B.DARK_OAK_LOG_ID, planks: B.DARK_OAK_PLANKS_ID },
  { log: B.MANGROVE_LOG_ID, planks: B.MANGROVE_PLANKS_ID },
  { log: B.CHERRY_LOG_ID, planks: B.CHERRY_PLANKS_ID },
  { log: B.PALE_OAK_LOG_ID, planks: B.PALE_OAK_PLANKS_ID },
];
const PLANKS: readonly number[] = WOODS.map((w) => w.planks);
const WOOL: readonly number[] = Object.values(B.WOOL_IDS);
const COBBLES: readonly number[] = [B.COBBLE_ID, B.COBBLED_DEEPSLATE_ID];
const SANDS: readonly number[] = [B.SAND_ID, B.RED_SAND_ID];
const COALS: readonly number[] = [I.COAL, I.CHARCOAL];

// --- builders --------------------------------------------------------------------

export const CRAFTING_RECIPES: Recipe[] = [];

/** Shaped recipe from rows of characters; `key` maps a char to an ingredient, ' ' = empty. */
function shaped(rows: string[], key: Record<string, Ingredient>, id: number, count = 1): void {
  if (id <= 0) return;
  const h = rows.length;
  const w = rows[0].length;
  const ing: Ingredient[] = [];
  for (const r of rows) for (const ch of r) {
    const v = ch === ' ' ? 0 : key[ch];
    if (v === undefined || (typeof v === 'number' && v < 0)) return; // references a block this build lacks
    ing.push(v);
  }
  CRAFTING_RECIPES.push({ shaped: true, w, h, ing, result: stackOf(id, count) });
}
function shapeless(ing: Ingredient[], id: number, count = 1): void {
  if (id <= 0 || ing.some((i) => typeof i === 'number' && i < 0)) return;
  CRAFTING_RECIPES.push({ shaped: false, w: 0, h: 0, ing, result: stackOf(id, count) });
}

registerBrewingRecipes(CRAFTING_RECIPES, PLANKS);

// --- wood -----------------------------------------------------------------------

for (const w of WOODS) shapeless([w.log], w.planks, 4);
shaped(['P', 'P'], { P: PLANKS }, I.STICK, 4);
shaped(['PP', 'PP'], { P: PLANKS }, B.CRAFTING_TABLE_ID);
shaped(['PPP', 'P P', 'PPP'], { P: PLANKS }, B.CHEST_ID);
shaped(['CCC', 'C C', 'CCC'], { C: COBBLES }, B.FURNACE_ID);
shaped(['C', 'S'], { C: COALS, S: I.STICK }, B.TORCH_ID, 4);
shaped(['C', 'S', 'D'], { C: COALS, S: I.STICK, D: [B.SOUL_SAND_ID, B.SOUL_SOIL_ID] }, B.SOUL_TORCH_ID, 4);
shaped(['PP', 'PP', 'PP'], { P: B.PLANKS_ID }, B.OAK_DOOR_BOTTOM_ID, 3);
shaped(['PP', 'PP', 'PP'], { P: B.SPRUCE_PLANKS_ID }, B.SPRUCE_DOOR_BOTTOM_ID, 3);
// --- shaped blocks (blocks.ts SHAPE_FAMILIES), Bedrock output counts --------------------------------
for (const fam of B.SHAPE_FAMILIES) {
  const X = fam.base, out = fam.canon;
  const wood = PLANKS.includes(X);
  switch (fam.kind) {
    case 'slab': shaped(['XXX'], { X }, out, 6); break;
    case 'stair': shaped(['X  ', 'XX ', 'XXX'], { X }, out, 4); break;
    case 'wall': shaped(['XXX', 'XXX'], { X }, out, 6); break;
    case 'fence':
      if (wood) shaped(['PSP', 'PSP'], { P: X, S: I.STICK }, out, 3);
      else shaped(['NBN', 'NBN'], { N: X, B: I.NETHER_BRICK }, out, 6);
      break;
    case 'gate': shaped(['SPS', 'SPS'], { P: X, S: I.STICK }, out, 1); break;
    case 'trapdoor':
      if (fam.name === 'Iron Trapdoor') shaped(['II', 'II'], { I: I.IRON_INGOT }, out, 1);
      else shaped(['PPP', 'PPP'], { P: X }, out, 2);
      break;
    case 'pane':
      if (fam.name === 'Glass Pane') shaped(['GGG', 'GGG'], { G: B.GLASS_ID }, out, 16);
      else shaped(['III', 'III'], { I: I.IRON_INGOT }, out, 16);
      break;
  }
}
for (const [kind, k] of B.DOOR_KINDS.entries()) {
  if (kind < 2) continue; // oak and spruce are above
  const planks = PLANKS.find((p) => B.BLOCKS[p]?.name === k.name.replace(' Door', ' Planks'));
  if (planks !== undefined) shaped(['PP', 'PP', 'PP'], { P: planks }, B.doorId(kind, 0, 0, false, false), 3);
}
// vanilla needs same-colour wool for a same-colour bed; only the red bed exists here
shaped(['WWW', 'PPP'], { W: WOOL, P: PLANKS }, B.BED_FOOT_ID);

// --- tools / weapons -----------------------------------------------------------------

const TOOL_MATS: [string, Ingredient][] = [
  ['WOODEN', PLANKS], ['STONE', COBBLES], ['IRON', I.IRON_INGOT], ['GOLDEN', I.GOLD_INGOT], ['DIAMOND', I.DIAMOND],
];
for (const [m, x] of TOOL_MATS) {
  const k = { X: x, S: I.STICK };
  shaped(['X', 'X', 'S'], k, (I as Record<string, number>)[`${m}_SWORD`]);
  shaped(['XXX', ' S ', ' S '], k, (I as Record<string, number>)[`${m}_PICKAXE`]);
  shaped(['XX', 'XS', ' S'], k, (I as Record<string, number>)[`${m}_AXE`]);
  shaped(['X', 'S', 'S'], k, (I as Record<string, number>)[`${m}_SHOVEL`]);
  shaped(['XX', ' S', ' S'], k, (I as Record<string, number>)[`${m}_HOE`]);
}
// spears: material top-right, stick centre, stick bottom-left (the matcher also accepts the mirror)
for (const t of SPEAR_KEYS) {
  const x: Ingredient = t === 'WOODEN' ? PLANKS : t === 'STONE' ? COBBLES : t === 'COPPER' ? I.COPPER_INGOT
    : t === 'IRON' ? I.IRON_INGOT : t === 'GOLDEN' ? I.GOLD_INGOT : I.DIAMOND;
  if (t !== 'NETHERITE') shaped(['  X', ' S ', 'S  '], { X: x, S: I.STICK }, (I as Record<string, number>)[`${t}_SPEAR`]);
}
shaped([' ST', 'S T', ' ST'], { S: I.STICK, T: I.STRING }, I.BOW);
shaped(['F', 'S', 'E'], { F: I.FLINT, S: I.STICK, E: I.FEATHER }, I.ARROW, 4);
shaped([' I', 'I '], { I: I.IRON_INGOT }, I.SHEARS);
shaped(['PIP', 'PPP', ' P '], { P: PLANKS, I: I.IRON_INGOT }, I.SHIELD);
shaped(['I I', ' I '], { I: I.IRON_INGOT }, I.BUCKET);
shapeless([I.IRON_INGOT, I.FLINT], I.FLINT_AND_STEEL);

// --- village workstations (entities/village); slabs -> planks/smooth stone (no slabs here) ---
{
  const LOGS = WOODS.map((w) => w.log);
  shaped(['P P', 'P P', 'PPP'], { P: PLANKS }, B.COMPOSTER_ID);
  shaped(['PPP', ' K ', ' P '], { P: PLANKS, K: B.BOOKSHELF_ID }, B.LECTERN_ID);
  shaped(['PSP', 'P P', 'PSP'], { P: PLANKS, S: I.STICK }, B.BARREL_ID);
  shaped(['FF', 'PP', 'PP'], { F: I.FLINT, P: PLANKS }, B.FLETCHING_TABLE_ID);
  shaped(['SMS', 'P P'], { S: I.STICK, M: 30, P: PLANKS }, B.GRINDSTONE_ID);
  shaped([' L ', 'LFL', ' L '], { L: LOGS, F: B.FURNACE_FACING_0 }, B.SMOKER_ID);
  shaped(['III', 'IFI', 'MMM'], { I: I.IRON_INGOT, F: B.FURNACE_FACING_0, M: 30 }, B.BLAST_FURNACE_ID);
  shaped([' I ', 'SSS'], { I: I.IRON_INGOT, S: B.STONE_ID }, B.STONECUTTER_ID);
}

// --- combat & enchanting ---------------------------------------------------------------
{
  // template duplication (vanilla): 7 diamonds + template + netherrack -> 2
  shaped(['DTD', 'DND', 'DDD'], { D: I.DIAMOND, T: I.NETHERITE_UPGRADE_SMITHING_TEMPLATE, N: B.NETHERRACK_ID }, I.NETHERITE_UPGRADE_SMITHING_TEMPLATE, 2);
  // copper tools/armor (26.x)
  const k = { X: I.COPPER_INGOT, S: I.STICK };
  shaped(['X', 'X', 'S'], k, I.COPPER_SWORD);
  shaped(['XXX', ' S ', ' S '], k, I.COPPER_PICKAXE);
  shaped(['XX', 'XS', ' S'], k, I.COPPER_AXE);
  shaped(['X', 'S', 'S'], k, I.COPPER_SHOVEL);
  shaped(['XX', ' S', ' S'], k, I.COPPER_HOE);
  shaped(['XXX', 'X X'], k, I.COPPER_HELMET);
  shaped(['X X', 'XXX', 'XXX'], k, I.COPPER_CHESTPLATE);
  shaped(['XXX', 'X X', 'X X'], k, I.COPPER_LEGGINGS);
  shaped(['X X', 'X X'], k, I.COPPER_BOOTS);
  // netherite ingot: 4 scrap + 4 gold (vanilla shapeless)
  shapeless([I.NETHERITE_SCRAP, I.NETHERITE_SCRAP, I.NETHERITE_SCRAP, I.NETHERITE_SCRAP,
    I.GOLD_INGOT, I.GOLD_INGOT, I.GOLD_INGOT, I.GOLD_INGOT], I.NETHERITE_INGOT);
  shaped(['II', 'PP', 'PP'], { I: I.IRON_INGOT, P: PLANKS }, B.SMITHING_TABLE_ID);
  // nether
  shaped(['NN', 'NN'], { N: I.NETHER_BRICK }, B.NETHER_BRICKS_ID);
  shaped(['QQ', 'QQ'], { Q: I.QUARTZ }, B.BLOCKS.find((b) => b?.name === 'Quartz Block')!.id);
  shapeless([I.BLAZE_ROD], I.BLAZE_POWDER, 2);
  shapeless([I.GUNPOWDER, I.BLAZE_POWDER, COALS], I.FIRE_CHARGE, 3); // vanilla: coal OR charcoal
  shapeless([I.BLAZE_POWDER, I.SLIME_BALL], I.MAGMA_CREAM); // vanilla magma_cream recipe
  shaped(['H', 'R'], { H: I.HEAVY_CORE, R: I.BREEZE_ROD }, I.MACE); // vanilla 1.21 mace: heavy core over a breeze rod
  shapeless([I.ENDER_PEARL, I.BLAZE_POWDER], I.EYE_OF_ENDER);
  shaped(['GGG', 'GEG', 'GTG'], { G: B.GLASS_ID, E: I.EYE_OF_ENDER, T: I.GHAST_TEAR }, I.END_CRYSTAL);
  shaped(['SSS'], { S: B.SUGAR_CANE_ID }, I.PAPER, 3);
  shaped(['NNN', 'NCN', 'NNN'], { N: I.GOLD_NUGGET, C: I.CARROT }, I.GOLDEN_CARROT);
  shapeless([I.BONE], I.BONE_MEAL, 3);
  shapeless([I.PAPER, I.PAPER, I.PAPER, I.LEATHER], I.BOOK);
  shapeless([I.GUNPOWDER, I.BONE_MEAL], I.FIREWORK_STAR); // dye colour: see dynamicCraft
  for (let n = 1; n <= 3; n++) {
    shapeless([I.PAPER, ...Array(n).fill(I.GUNPOWDER)], I.FIREWORK_ROCKET, 3);
    CRAFTING_RECIPES[CRAFTING_RECIPES.length - 1].result.fw = [n]; // flight duration
  }
  shaped(['PPP', 'BBB', 'PPP'], { P: PLANKS, B: I.BOOK }, B.BOOKSHELF_ID);
  shaped([' B ', 'DOD', 'OOO'], { B: I.BOOK, D: I.DIAMOND, O: B.OBSIDIAN_ID }, B.ENCHANTING_TABLE_ID);
  shaped(['SIS', 'TRT', ' S '], { S: I.STICK, I: I.IRON_INGOT, T: I.STRING, R: I.IRON_INGOT }, I.CROSSBOW); // DEVIATION: no tripwire hook block yet; iron ingot stands in
  shaped(['BBB', ' I ', 'III'], { B: named('Block of Iron'), I: I.IRON_INGOT }, B.ANVIL_ID);
}

// --- structures round 2 ---------------------------------------------------------------
shaped(['III', 'III'], { I: I.IRON_INGOT }, B.IRON_BARS_ID, 16);
shaped(['PP', 'PP'], { P: I.POPPED_CHORUS_FRUIT }, B.PURPUR_BLOCK_ID, 4);
shaped(['P', 'P'], { P: B.PURPUR_BLOCK_ID }, B.PURPUR_PILLAR_ID, 1);

// --- armor ---------------------------------------------------------------------------

for (const [m, x] of [['LEATHER', I.LEATHER], ['IRON', I.IRON_INGOT], ['GOLDEN', I.GOLD_INGOT], ['DIAMOND', I.DIAMOND]] as [string, Ingredient][]) {
  const k = { X: x };
  const id = (p: string): number => (I as Record<string, number>)[`${m}_${p}`];
  shaped(['XXX', 'X X'], k, id('HELMET'));
  shaped(['X X', 'XXX', 'XXX'], k, id('CHESTPLATE'));
  shaped(['XXX', 'X X', 'X X'], k, id('LEGGINGS'));
  shaped(['X X', 'X X'], k, id('BOOTS'));
}

// --- food ----------------------------------------------------------------------------

shaped(['WWW'], { W: I.WHEAT }, I.BREAD);
shaped(['GGG', 'GAG', 'GGG'], { G: I.GOLD_INGOT, A: I.APPLE }, I.GOLDEN_APPLE);
shaped(['MMM', 'MMM', 'MMM'], { M: I.MELON_SLICE }, B.MELON_ID);

// --- storage blocks <-> items --------------------------------------------------------

const COMPRESS: [string, number][] = [
  ['Block of Iron', I.IRON_INGOT], ['Block of Gold', I.GOLD_INGOT], ['Block of Diamond', I.DIAMOND],
  ['Block of Emerald', I.EMERALD], ['Block of Lapis', I.LAPIS_LAZULI], ['Block of Redstone', I.REDSTONE],
  ['Block of Coal', I.COAL], ['Block of Copper', I.COPPER_INGOT],
  ['Block of Raw Iron', I.RAW_IRON], ['Block of Raw Gold', I.RAW_GOLD], ['Block of Raw Copper', I.RAW_COPPER],
];
for (const [bn, item] of COMPRESS) {
  const b = named(bn);
  shaped(['XXX', 'XXX', 'XXX'], { X: item }, b);
  shapeless([b], item, 9);
}
shaped(['XXX', 'XXX', 'XXX'], { X: I.IRON_NUGGET }, I.IRON_INGOT);
shapeless([I.IRON_INGOT], I.IRON_NUGGET, 9);
shaped(['XXX', 'XXX', 'XXX'], { X: I.GOLD_NUGGET }, I.GOLD_INGOT);
shapeless([I.GOLD_INGOT], I.GOLD_NUGGET, 9);
shaped(['XXX', 'XXX', 'XXX'], { X: I.WHEAT }, B.HAY_BALE_ID);
shapeless([B.HAY_BALE_ID], I.WHEAT, 9);

// --- building blocks ------------------------------------------------------------------

shaped(['BB', 'BB'], { B: I.BRICK }, B.BRICKS_ID);
shaped(['SS', 'SS'], { S: B.STONE_ID }, B.STONE_BRICK_ID, 4);
shaped(['SS', 'SS'], { S: SANDS[0] }, B.SANDSTONE_ID);
shaped(['SS', 'SS'], { S: B.RED_SAND_ID }, B.RED_SANDSTONE_ID);
shaped(['SS', 'SS'], { S: B.SANDSTONE_ID }, named('Cut Sandstone'), 4);
shaped(['SS', 'SS'], { S: B.RED_SANDSTONE_ID }, named('Cut Red Sandstone'), 4);
shaped(['SS', 'SS'], { S: B.GRANITE_ID }, named('Polished Granite'), 4);
shaped(['SS', 'SS'], { S: B.DIORITE_ID }, named('Polished Diorite'), 4);
shaped(['SS', 'SS'], { S: B.ANDESITE_ID }, named('Polished Andesite'), 4);
shaped(['SS', 'SS'], { S: B.COBBLED_DEEPSLATE_ID }, named('Polished Deepslate'), 4);
shaped(['SS', 'SS'], { S: named('Polished Deepslate') }, named('Deepslate Bricks'), 4);
shaped(['SS', 'SS'], { S: I.CLAY_BALL }, B.CLAY_ID);
shaped(['SS', 'SS'], { S: I.STRING }, B.WOOL_IDS.white);
shaped(['DG', 'GD'], { D: B.DIRT_ID, G: B.GRAVEL_ID }, B.COARSE_DIRT_ID, 4);
shapeless([B.COBBLE_ID, B.MOSS_ID], B.MOSSY_COBBLE_ID);
shapeless([B.STONE_BRICK_ID, B.MOSS_ID], B.MOSSY_BRICKS_ID);
shaped(['GSG', 'SGS', 'GSG'], { G: I.GUNPOWDER, S: SANDS }, B.TNT_ID);
shaped(['P', 'T'], { P: B.PUMPKIN_ID, T: B.TORCH_ID }, named("Jack o'Lantern"));
shaped(['I I', 'ISI', 'I I'], { I: I.IRON_INGOT, S: I.STICK }, B.RAIL_ID, 16);
shaped(['G G', 'GSG', 'GRG'], { G: I.GOLD_INGOT, S: I.STICK, R: I.REDSTONE }, B.POWERED_RAIL_ID, 6);
// redstone (world/redstone)
shaped(['R', 'S'], { R: I.REDSTONE, S: I.STICK }, RS.TORCH);
shaped(['S', 'C'], { S: I.STICK, C: B.COBBLE_ID }, RS.LEVER);
shapeless([B.STONE_ID], RS.STONE_BUTTON);
shapeless([PLANKS as number[]], RS.OAK_BUTTON);
shaped(['SS'], { S: B.STONE_ID }, RS.STONE_PLATE);
shaped(['PP'], { P: PLANKS }, RS.OAK_PLATE);
shaped(['GG'], { G: I.GOLD_INGOT }, RS.LIGHT_PLATE);
shaped(['II'], { I: I.IRON_INGOT }, RS.HEAVY_PLATE);
shaped(['TRT', 'SSS'], { T: RS.TORCH, R: I.REDSTONE, S: B.STONE_ID }, RS.REPEATER);
shaped([' T ', 'TQT', 'SSS'], { T: RS.TORCH, Q: I.QUARTZ, S: B.STONE_ID }, RS.COMPARATOR);
shaped([' R ', 'RGR', ' R '], { R: I.REDSTONE, G: B.GLOWSTONE_ID }, RS.LAMP);
shaped(['CCC', 'RRQ', 'CCC'], { C: B.COBBLE_ID, R: I.REDSTONE, Q: I.QUARTZ }, RS.OBSERVER);
shaped(['PPP', 'CIC', 'CRC'], { P: PLANKS, C: B.COBBLE_ID, I: I.IRON_INGOT, R: I.REDSTONE }, RS.PISTON);
shaped(['S', 'P'], { S: I.SLIME_BALL, P: RS.PISTON }, RS.STICKY_PISTON);
shaped(['PPP', 'PRP', 'PPP'], { P: PLANKS, R: I.REDSTONE }, RS.NOTE);
shaped([' R ', 'RHR', ' R '], { R: I.REDSTONE, H: B.HAY_BALE_ID }, RS.TARGET);
shaped(['GGG', 'QQQ', 'SSS'], { G: B.GLASS_ID, Q: I.QUARTZ, S: B.SHAPE_FAMILIES.filter((f) => f.kind === 'slab' && PLANKS.includes(f.base)).map((f) => f.canon) }, RS.DAYLIGHT);
shaped(['I', 'S', 'P'], { I: I.IRON_INGOT, S: I.STICK, P: PLANKS }, RS.HOOK, 2);
shaped(['II', 'II', 'II'], { I: I.IRON_INGOT }, B.IRON_DOOR_ID, 3);
shaped(['I I', 'IPI', 'IRI'], { I: I.IRON_INGOT, P: RS.STONE_PLATE, R: I.REDSTONE }, B.DETECTOR_RAIL_ID, 6);
shaped(['ISI', 'ITI', 'ISI'], { I: I.IRON_INGOT, S: I.STICK, T: RS.TORCH }, B.ACTIVATOR_RAIL_ID, 6);
// vehicles & fishing (entities/vehicles). Bedrock boats need a wooden shovel in the top middle; the shovel is consumed.
shaped(['  S', ' ST', 'S T'], { S: I.STICK, T: I.STRING }, I.FISHING_ROD);
shaped(['SS ', 'SL ', '  S'], { S: I.STRING, L: I.SLIME_BALL }, I.LEAD, 2);
shaped(['I I', 'III'], { I: I.IRON_INGOT }, I.MINECART);
// riding (game/mounts.ts): fishing rod + carrot / warped fungus
shaped(['R ', ' C'], { R: I.FISHING_ROD, C: I.CARROT }, I.CARROT_ON_A_STICK);
shaped(['R ', ' F'], { R: I.FISHING_ROD, F: B.WARPED_FUNGUS_ID }, I.WARPED_FUNGUS_ON_A_STICK);
WOODS.forEach((w, i) => {
  const boat = I[(['OAK', 'SPRUCE', 'BIRCH', 'JUNGLE', 'ACACIA', 'DARK_OAK', 'MANGROVE', 'CHERRY', 'PALE_OAK'][i] + '_BOAT') as 'OAK_BOAT'];
  shaped(['PSP', 'PPP'], { P: w.planks, S: I.WOODEN_SHOVEL }, boat);
});

// --- containers & utility blocks (game/utility), Java recipes --------------------------------
shapeless([B.CHEST_ID, RS.HOOK], UTIL.TRAPPED_CHEST);
shaped(['OOO', 'OEO', 'OOO'], { O: B.OBSIDIAN_ID, E: I.EYE_OF_ENDER }, UTIL.ENDER_CHEST);
shaped(['I I', 'ICI', ' I '], { I: I.IRON_INGOT, C: B.CHEST_ID }, UTIL.HOPPER);
shaped(['CCC', 'CBC', 'CRC'], { C: B.COBBLE_ID, B: I.BOW, R: I.REDSTONE }, UTIL.DISPENSER);
shaped(['CCC', 'C C', 'CRC'], { C: B.COBBLE_ID, R: I.REDSTONE }, UTIL.DROPPER);
shaped(['S', 'C', 'S'], { S: I.SHULKER_SHELL, C: B.CHEST_ID }, UTIL.SHULKER);
shaped(['I I', 'I I', 'III'], { I: I.IRON_INGOT }, UTIL.CAULDRON);
shaped(['MMM', 'SES', 'WWW'], { M: I.MILK_BUCKET, S: I.SUGAR, E: I.EGG, W: I.WHEAT }, UTIL.CAKE);
shaped(['SSS', 'SLS', 'SSS'], { S: I.STICK, L: I.LEATHER }, UTIL.FRAME);
shaped(['B B', ' B '], { B: I.BRICK }, UTIL.POT);
shaped(['S S', 'SSS', 'S S'], { S: I.STICK }, UTIL.LADDER, 3);
shaped(['N', 'I', 'N'], { N: I.IRON_NUGGET, I: I.IRON_INGOT }, UTIL.CHAIN);
shaped(['NNN', 'NTN', 'NNN'], { N: I.IRON_NUGGET, T: B.TORCH_ID }, UTIL.LANTERN);
shaped(['NNN', 'NTN', 'NNN'], { N: I.IRON_NUGGET, T: named('Soul Torch') }, UTIL.SOUL_LANTERN);
shaped([' S ', 'SCS', 'LLL'], { S: I.STICK, C: COALS, L: WOODS.map((w) => w.log) }, UTIL.CAMPFIRE);
shaped(['C', 'C', 'C'], { C: I.COPPER_INGOT }, UTIL.ROD);
WOODS.forEach((w, i) => shaped(['PPP', 'PPP', ' S '], { P: w.planks, S: I.STICK }, SIGN_IDS[i] ?? -1, 3));

// --- matcher --------------------------------------------------------------------------

const fits = (ing: Ingredient, s: Stack | undefined): boolean => {
  const present = s !== undefined && !isEmpty(s);
  if (ing === 0) return !present;
  if (!present) return false;
  return typeof ing === 'number' ? ing === s.id : ing.includes(s.id);
};

function matchShapedAt(r: Recipe, cells: Stack[], gw: number, x0: number, y0: number, mirror: boolean): boolean {
  for (let y = 0; y < r.h; y++) {
    for (let x = 0; x < r.w; x++) {
      const ing = r.ing[y * r.w + (mirror ? r.w - 1 - x : x)];
      if (!fits(ing, cells[(y0 + y) * gw + x0 + x])) return false;
    }
  }
  return true;
}

function matchShapeless(ing: readonly Ingredient[], stacks: Stack[]): boolean {
  if (ing.length !== stacks.length) return false;
  const used = new Array<boolean>(ing.length).fill(false);
  const go = (si: number): boolean => {
    if (si === stacks.length) return true;
    for (let k = 0; k < ing.length; k++) {
      if (used[k] || !fits(ing[k], stacks[si])) continue;
      used[k] = true;
      if (go(si + 1)) return true;
      used[k] = false;
    }
    return false;
  };
  return go(0);
}

function find(grid: readonly Stack[], w: number, h: number): Recipe | null {
  let minX = w, minY = h, maxX = -1, maxY = -1, n = 0;
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    if (isEmpty(grid[y * w + x])) continue;
    n++;
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  }
  if (n === 0) return null;
  const bw = maxX - minX + 1, bh = maxY - minY + 1;
  const stacks = grid.filter((s) => !isEmpty(s));
  const cells = grid as Stack[];
  for (const r of CRAFTING_RECIPES) {
    if (r.shaped) {
      if (r.w !== bw || r.h !== bh) continue;
      if (matchShapedAt(r, cells, w, minX, minY, false) || matchShapedAt(r, cells, w, minX, minY, true)) return r;
    } else if (r.ing.length === n && matchShapeless(r.ing, stacks)) return r;
  }
  return null;
}

/** Result of the grid (a fresh Stack), or null when no recipe matches. */
function dynamicCraft(grid: readonly Stack[]): Stack | null {
  const st = grid.filter((s) => !isEmpty(s));
  const cnt = (id: number): number => st.filter((s) => s.id === id).length;
  const dyes = st.filter((s) => dyeColour(s.id) !== undefined);
  // star: gunpowder + one dye
  if (st.length === 2 && cnt(I.GUNPOWDER) === 1 && dyes.length === 1) {
    const res = stackOf(I.FIREWORK_STAR, 1); res.fw = [dyeColour(dyes[0].id)!]; return res;
  }
  // rocket: 1 paper + 1..3 gunpowder + any number of stars
  const stars = st.filter((s) => s.id === I.FIREWORK_STAR);
  const gp = cnt(I.GUNPOWDER);
  if (stars.length > 0 && cnt(I.PAPER) === 1 && gp >= 1 && gp <= 3 && st.length === 1 + gp + stars.length) {
    const res = stackOf(I.FIREWORK_ROCKET, 3); res.fw = [gp, ...stars.map((s) => s.fw?.[0] ?? 0xffffff)]; return res;
  }
  return null;
}

export function craft(grid: readonly Stack[], w: number, h: number): Stack | null {
  const dyn = dynamicCraft(grid);
  if (dyn) return dyn;
  const r = find(grid, w, h);
  return r ? cloneStack(r.result) : null;
}

/** Remove one of every ingredient the (matching) recipe uses. */
export function consumeCraft(grid: Stack[], w: number, h: number): void {
  for (let i = 0; i < w * h; i++) {
    const s = grid[i];
    if (isEmpty(s)) { if (s.count !== 0 || s.id !== 0) clearStack(s); continue; }
    if (--s.count <= 0) clearStack(s);
  }
}
